import {
  Drawer,
  StateCount,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ideon-api/ideon-ui'
import { history, QUARTERS, type Audience, type PlanEntitlements } from '../../lib/plan-entitlements'
import { productLineLabel } from '../../lib/product-lines'
import { audienceLabel, countStates, isQuarterlyAudience } from './CoverageLine'

interface Props {
  isOpen: boolean
  onClose: () => void
  productLine: string
  audience: Audience
  permissionSetKey: string
  // The already-fetched response; the drawer never requests.
  plans: PlanEntitlements | null | undefined
}

export function CoverageHistoryDrawer({ isOpen, onClose, productLine, audience, permissionSetKey, plans }: Props) {
  const title = `Coverage history · ${productLineLabel(productLine)} · ${audienceLabel(audience)} · ${permissionSetKey}`
  const periods = isQuarterlyAudience(audience) ? [...QUARTERS] : (['Annual'] as const)
  const byYear = new Map(history(plans, productLine, audience).map((r) => [Number(r.year), r.periods]))
  // Continuous from the earliest year with data through next year, so a gap reads as a gap.
  const first = Math.min(...byYear.keys())
  const last = Math.max(new Date().getFullYear() + 1, ...byYear.keys())
  const years = byYear.size ? Array.from({ length: last - first + 1 }, (_, i) => first + i) : []

  return (
    <Drawer.Root isOpen={isOpen} onClose={onClose} size="lg" testIdPrefix="coverage-history-drawer">
      <Drawer.Header className="mb-4 border-b border-ideon-gray-40 pb-4">
        <Drawer.Title className="min-w-0 truncate text-lg leading-7"><span title={title}>{title}</span></Drawer.Title>
        <Drawer.CloseButton />
      </Drawer.Header>
      <Drawer.Body>
        <div className="overflow-x-auto">
        <Table variant="bordered">
          <TableHeader>
            <TableRow>
              <TableHead>Year</TableHead>
              {periods.map((p) => (
                <TableHead key={p}>{p}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {years.map((year) => {
              const byPeriod = byYear.get(year) ?? {}
              return (
                <TableRow key={year}>
                  <th scope="row" className="p-4 text-left font-semibold text-ideon-gray-black">
                    {year}
                  </th>
                  {periods.map((p) => {
                    const n = countStates(byPeriod[p] ?? [])
                    return <TableCell key={p}>{n ? <StateCount states={n} /> : '—'}</TableCell>
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        </div>
      </Drawer.Body>
    </Drawer.Root>
  )
}
